import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse, AxiosError } from "axios";
import Problem from "../types/problem";
import { StationDTO } from "../types/stationDTO";
import apiClient from "../main/apiClient";

const useFetchStations = (organisationId: string) => {
    return useQuery<StationDTO[], AxiosError>({
        queryKey: ["station"],
        queryFn: () =>
          apiClient.get(`/organisation/${organisationId}/station`).then((resp) => resp.data),
    });
}

const useFetchStation = (organisationId: string, stationId: string) => {
  return useQuery<StationDTO, AxiosError<Problem>>({
    queryKey: ["station", stationId],
    queryFn: () =>
      apiClient.get(`/organisation/${organisationId}/station/${stationId}`).then((resp) => resp.data),
  });
};

const useUpdateStation = (organisationID: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, StationDTO>({
    mutationFn: (station) => apiClient.put(`/organisation/${organisationID}/station`, station),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["station"] });
    },
    onError: (error) => {
      console.log(error);
    }
  });
};

const useAddStation = (organisationId: string, setStationState: (station: StationDTO) => void) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, StationDTO>({
    mutationFn: (station) => apiClient.post(`/organisation/${organisationId}/station`, station),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stations", organisationId] });
    },
    onError: (error, variables) => {
      console.log(error);
      setStationState(variables); // Preserve the state on error
    }
  });
};

const useDeleteStation = (organisationId: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, StationDTO>({
    mutationFn: (station) => apiClient.delete(`/organisation/${organisationId}/station`, { data: station }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["station"] });
      queryClient.invalidateQueries({ queryKey: ["unit"] });
      queryClient.invalidateQueries({ queryKey: ["terminal"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnits"] });
      queryClient.invalidateQueries({ queryKey: ["capabilitiesForUnitMission"] });
    }
  });
};

export { useFetchStations, useFetchStation, useUpdateStation, useAddStation, useDeleteStation }
